/**
 * Sticker card: a flat-vector raster emblem with an optional text caption
 * below. Render modes:
 *
 *   1. **Earned (raster)** — a transparent PNG from `STICKER_PNGS`
 *      (src/assets/stickers/index.ts), keyed by the backend achievement id.
 *
 *   2. **Framed (mastery / alphabet)** — a shared frame image with the
 *      mastered word / letter drawn as a live text overlay in the blank
 *      centre. One frame serves every word and all 26 letters.
 *
 *   3. **Silhouette (streak/total unearned)** — a stripped-down geometric
 *      shape (no number) so the milestone can't leak through, preserving
 *      the surprise-reward principle in spec §8.
 *
 * School-year stickers use mode 1 in both states: there's no surprise to
 * protect, so an unearned one renders its full art dimmed via `locked`
 * rather than becoming a silhouette.
 *
 * Caption (the sticker's name) renders as a normal text block underneath —
 * except for framed stickers (the word/letter is already on the art) and
 * silhouettes (deliberately nameless).
 *
 * Chip overlays ("new!" top-right, "× N" tally bottom-right) anchor to the
 * emblem corners.
 */
import { useMemo } from "react";

import { STICKER_FRAMES, STICKER_PNGS } from "@/assets/stickers";
import type { AchievementCategory } from "@/types/data";
import "./StickerEmblem.css";

export type StickerCategory = AchievementCategory | "per_session" | "alphabet";

export interface StickerEmblemProps {
  /** Backend achievement category. */
  category: StickerCategory;
  /** Backend achievement id (e.g. "first_practice", "streak_7",
   *  "mastered_cat", "letter_a"). Mastery + alphabet resolve to a shared
   *  frame; the word/letter comes from `name`. */
  stickerId: string;
  /** Display name — caption beneath the emblem, or the overlay text for
   *  framed (mastery word / alphabet letter) stickers. */
  name: string;
  /** True when this is unearned streak/total — show silhouette only. */
  silhouette?: boolean;
  /** Per-session zero-count / unearned-letter rendering — dim, keep text. */
  locked?: boolean;
  /** Tally count for per-session stickers. Renders "× N" chip. */
  count?: number;
  /** "new!" flag — shown for any not-yet-seen earned sticker. */
  isNew?: boolean;
  /** Optional extra classes (e.g. grid-cell sizing). */
  className?: string;
}

export function StickerEmblem({
  category,
  stickerId,
  name,
  silhouette = false,
  locked = false,
  count,
  isNew = false,
  className,
}: StickerEmblemProps) {
  const stateClasses = [
    silhouette ? "is-silhouette" : "",
    locked ? "is-locked" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const cls = ["sticker-card", `sticker-card--${category}`, stateClasses, className]
    .filter(Boolean)
    .join(" ");

  const isMastery = category === "mastery";
  const isAlphabet = category === "alphabet";
  const isFramed = isMastery || isAlphabet;
  // The mastery achievement name is "Mastered: <word>"; inside the hexagon
  // we only want the bare word (the badge already means "mastered").
  const overlayText = isMastery ? name.replace(/^Mastered:\s*/i, "") : name;
  const frameSrc = isMastery
    ? STICKER_FRAMES.mastery
    : isAlphabet
      ? STICKER_FRAMES.alphabet
      : null;

  const rasterSrc = useMemo(
    () => (silhouette || isFramed ? null : (STICKER_PNGS[stickerId] ?? null)),
    [silhouette, isFramed, stickerId],
  );

  return (
    <div className={cls} role="img" aria-label={ariaLabel(category, name, silhouette, locked, count)}>
      <div className="sticker-card__emblem">
        {isFramed && frameSrc !== null ? (
          <div className="sticker-card__framed">
            <img className="sticker-card__img" src={frameSrc} alt="" draggable={false} />
            <span className={`sticker-card__overlay sticker-card__overlay--${category}`}>
              {overlayText}
            </span>
          </div>
        ) : rasterSrc !== null ? (
          <img className="sticker-card__img" src={rasterSrc} alt="" draggable={false} />
        ) : (
          // Silhouette fallback for unearned streak / total. Generic shape
          // so the milestone number doesn't leak through.
          <svg
            viewBox="0 0 100 100"
            className="sticker-card__silhouette-svg"
            aria-hidden="true"
            focusable="false"
          >
            {category === "streak" && <SilhouetteCircle />}
            {category === "total" && <SilhouetteRibbon />}
          </svg>
        )}
        {isNew && <span className="sticker-card__new">new!</span>}
        {count !== undefined && count > 0 && (
          <span className="sticker-card__count">×{count}</span>
        )}
      </div>
      {/* Framed stickers carry their text on the art; silhouettes are
          deliberately nameless. Everything else gets a caption. */}
      {!silhouette && !isFramed && <div className="sticker-card__caption">{name}</div>}
    </div>
  );
}

// ─── Silhouette shapes (bare, no focal mark) ────────────────────────

function SilhouetteCircle() {
  return (
    <>
      <circle className="sticker-card__sil-fill" cx="50" cy="50" r="40" />
      <circle className="sticker-card__sil-outline" cx="50" cy="50" r="40" />
    </>
  );
}

function SilhouetteRibbon() {
  // Same shape language as the earned ribbon: medallion + two tails.
  return (
    <>
      <path
        className="sticker-card__sil-fill"
        d="M 28 56 L 18 88 L 30 80 L 36 92 L 46 64 Z"
      />
      <path
        className="sticker-card__sil-fill"
        d="M 72 56 L 82 88 L 70 80 L 64 92 L 54 64 Z"
      />
      <circle className="sticker-card__sil-fill" cx="50" cy="42" r="28" />
      <circle className="sticker-card__sil-outline" cx="50" cy="42" r="28" />
    </>
  );
}

// ─── A11y label ─────────────────────────────────────────────────────

function ariaLabel(
  category: StickerCategory,
  name: string,
  silhouette: boolean,
  locked: boolean,
  count?: number,
): string {
  if (silhouette || locked) return `${name} (not yet earned)`;
  const suffix = count !== undefined && count > 0 ? ` (earned ${count} times)` : "";
  const prefix =
    category === "first" ? "Sticker — first: " :
    category === "per_session" ? "Sticker — per session: " :
    category === "streak" ? "Sticker — streak: " :
    category === "mastery" ? "Sticker — mastered: " :
    category === "alphabet" ? "Letter: " :
    category === "school" ? "Sticker — school year: " :
    "Sticker — total: ";
  return `${prefix}${name}${suffix}`;
}
